import { useQuery } from "@tanstack/react-query";
import { api, apiOptional } from "../api/client";
import { useAuthStore } from "../store/auth";
import type { CatalogItem, CatalogResponse, TokensResponse } from "../types/models";

// Загрузка каталога после входа — порт boot() из app-core.js (/api/tokens + /api/catalog + опц. /api/cross/catalog).
// Cross-каталог — через apiOptional (фича-эндпоинт; его 401/403 НЕ разлогинивает). Каталог статичен → staleTime: Infinity.
export interface Bootstrap {
  tokens: string[];
  timeframes: string[];
  /** "BTC" → "BTC:USDT" (TOKEN_MAP). */
  tokenMap: Record<string, string>;
  catalog: CatalogItem[];
  crossCatalog: CatalogItem[];
  labels: Record<string, string>;
  shortLabels: Record<string, string>;
}

export function useBootstrap() {
  const authed = useAuthStore((s) => !!s.token && s.user !== null);
  return useQuery({
    queryKey: ["bootstrap"],
    enabled: authed,
    staleTime: Number.POSITIVE_INFINITY,
    queryFn: async (): Promise<Bootstrap> => {
      const [tokRes, catRes] = await Promise.all([api.GET("/api/tokens"), api.GET("/api/catalog")]);
      if (tokRes.error || !tokRes.data) throw new Error("Не удалось загрузить токены");
      if (catRes.error || !catRes.data) throw new Error("Не удалось загрузить каталог");
      const tok = tokRes.data as TokensResponse;
      const cat = catRes.data as CatalogResponse;
      // cross-каталог опционален: сбой/403 → пустой (фича-эндпоинт не должен ронять bootstrap)
      let crossCatalog: CatalogItem[] = [];
      try {
        const cx = await apiOptional.GET("/api/cross/catalog");
        if (!cx.error && cx.data) crossCatalog = (cx.data as CatalogResponse).catalog || [];
      } catch {
        crossCatalog = [];
      }
      const tokenMap: Record<string, string> = {};
      for (const t of tok.tokens) tokenMap[t.replace(":USDT", "")] = t;
      const labels: Record<string, string> = {};
      const shortLabels: Record<string, string> = {};
      for (const c of [...(cat.catalog || []), ...crossCatalog]) {
        labels[c.id] = c.label;
        shortLabels[c.id] = c.short_label || c.short || c.short_name || c.label;
      }
      return {
        tokens: tok.tokens,
        timeframes: tok.timeframes,
        tokenMap,
        catalog: cat.catalog,
        crossCatalog,
        labels,
        shortLabels,
      };
    },
  });
}
